import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { DocumentNode } from "graphql"
import { ApolloClient } from "@apollo/client"
import { useQueryUser } from "@hydrologiq/shared/providers/QueryProvider"
import { useDebounce } from "./useDebounce"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useGraphQLClient } from "./useGraphQLClient"

export type QueryType =
  | "assets"
  | "availableAssets"
  | "customers"
  | "customersOnly"
  | "fillingSites"
  | "sites"
  | "batches"
  | "orders"

export interface SearchQueryOptions {
  queryType: QueryType
  searchTerm: string
  page?: number
  perPage?: number
  debounceMs?: number
  enabled?: boolean
  allowEmptySearch?: boolean // Allow query with empty search term for initial loads
  // Additional variables for specific query types
  additionalVariables?: Record<string, any>
}

export interface AssetSearchResult {
  id: string
  name: string
}

export interface CustomerSearchResult {
  id: string
  name: string
  sites?: Array<{
    id: string
    name: string
  }>
}

type SearchResult = AssetSearchResult | CustomerSearchResult

const executeGraphQLQuery = async <TData = any>(
  client: ApolloClient<any>,
  document: DocumentNode,
  variables: Record<string, any>,
  authToken?: string,
): Promise<TData> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }

  if (authToken) {
    headers.authorization = `Bearer ${authToken}`
  }

  try {
    const result = await client.query({
      query: document,
      variables,
      fetchPolicy: "network-only",
    })

    return result.data
  } catch (error) {
    console.error(error, "error")
    throw error
  }
}

const getQueryDocument = (queryType: QueryType) => {
  switch (queryType) {
    case "assets":
      return gqlTypes.SearchAssetsDocument
    case "availableAssets":
      return gqlTypes.GetAvailableAssetsDocument
    case "customers":
      return gqlTypes.SearchCustomersDocument
    case "customersOnly":
      return gqlTypes.SearchCustomersDocument
    case "fillingSites":
      return gqlTypes.GetFillingSitesDocument
    case "sites":
      return gqlTypes.SearchSitesDocument
    case "batches":
      return gqlTypes.GetBatchesDocument
    case "orders":
      return gqlTypes.GetOrdersDocument
    default:
      throw new Error(`Unknown query type: ${queryType}`)
  }
}

const getQueryEndpoint = (queryType: QueryType): "primary" | "legacy" => {
  // All these queries are now migrated to primary endpoint
  switch (queryType) {
    case "assets":
    case "availableAssets":
    case "customers":
    case "customersOnly":
    case "fillingSites":
    case "sites":
    case "batches":
    case "orders":
      return "primary"
    default:
      return "legacy"
  }
}

const extractData = (queryType: QueryType, data: any): SearchResult[] => {
  switch (queryType) {
    case "assets":
    case "availableAssets":
      return data?.assets?.data || []
    case "customers":
      // Transform customer data to flatten sites with customer info
      const customers = data?.customers?.data || []
      return customers
        .map(
          (customer: any) =>
            customer.sites?.map((site: any) => ({
              customer,
              id: site.id,
              name: site.name,
            })) || [],
        )
        .flat()
    case "customersOnly":
      // Return customers without flattening sites
      const customersOnly = data?.customers?.data || []
      return customersOnly.map((customer: any) => ({
        id: customer.id,
        name: customer.name,
        sites: customer.sites || [],
      }))
    case "fillingSites":
      // Transform fill sites data to match expected format
      const fillingSites = data?.fillingSites?.data || []
      return fillingSites.map((fs: any) => ({
        id: fs.site.id,
        name: fs.site.name,
      }))
    case "sites":
      // Transform unified sites data
      const sites = data?.sites?.data || []
      return sites.map((site: any) => ({
        id: site.id,
        name: site.name,
        location: site.location,
        types: site.types || [],
        customers: site.customers || [],
        is_customer_site: site.is_customer_site || false,
        is_filling_site: site.is_filling_site || false,
      }))
    case "batches":
      // Transform batches data
      const batches = data?.batches?.data || []
      return batches.map((batch: any) => ({
        id: batch.id,
        name: batch.name,
        quantity: batch.quantity,
        created_at: batch.created_at,
      }))
    case "orders":
      // Transform orders data
      const orders = data?.orders?.data || []
      return orders.map((order: any) => ({
        id: order.id,
        name: order.name,
        start: order.start,
        end: order.end,
        status: order.status,
        assets: order.assets,
        sites: order.sites,
        quantity: order.quantity,
      }))
    default:
      return []
  }
}

export const useSearchQuery = ({
  queryType,
  searchTerm,
  page = 1,
  perPage = 20,
  debounceMs = 300,
  enabled = true,
  allowEmptySearch = false,
  additionalVariables = {},
}: SearchQueryOptions) => {
  const user = useQueryUser()
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs)

  const queryKey = useMemo(
    () => [queryType, "search", debouncedSearchTerm, page, perPage, additionalVariables],
    [queryType, debouncedSearchTerm, page, perPage, additionalVariables],
  )

  const queryVariables = useMemo(
    () => ({
      query: debouncedSearchTerm,
      page,
      per_page: perPage,
      ...additionalVariables,
    }),
    [debouncedSearchTerm, page, perPage, additionalVariables],
  )

  const shouldEnable = enabled && (allowEmptySearch || debouncedSearchTerm.trim() !== "")
  // Use primary endpoint for migrated queries, legacy for others
  const endpoint = getQueryEndpoint(queryType)
  const graphQLClient = useGraphQLClient(endpoint)

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const document = getQueryDocument(queryType)
      const token = user && user?.firebaseUser ? (await user.firebaseUser.getIdTokenResult()).token : undefined
      const data = await executeGraphQLQuery(graphQLClient, document, queryVariables, token)
      return extractData(queryType, data)
    },
    enabled: shouldEnable,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    isError: query.isError,
    isSuccess: query.isSuccess,
  }
}
